/**
 * Budget · Pul (docs/12-moliya.md): hisoblar (naqd, karta, bank) va qoldiqlari, USD/UZS kursi, kirim-chiqim daftari.
 * Qoldiq serverda daftardan hisoblanadi; har o'zgarish real-time (hamma oyna bir xil ko'radi). Hisob ochish va qoldiqni
 * tuzatish -- owner; filial menejeri o'z filiali hisoblariga kirim va o'tkazma yozadi.
 */
import { usePageSize } from "@/lib/pageSize";
import { forwardRef, useMemo, useState, type HTMLAttributes } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Banknote, CreditCard, Landmark, Pencil, Plus, Search, type LucideIcon } from "lucide-react";
import { cn, dayjs, formatDate } from "@/lib/utils";
import { useDeleteAccount, useFinanceOverview, useMoneyAccounts, useTransactions, useVoidMove } from "@/features/finance/api";
import { categoryLabel, fmt, kindKey, num, timeText } from "@/features/finance/money";
import { accountMenu, transactionMenu } from "@/features/finance/menu";
import type { AccountKind, FinanceOverview, MoneyAccount, Transaction } from "@/features/finance/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { confirmDialog } from "@/components/ui/confirm";
import { ContextMenu } from "@/components/ui/context-menu";
import { Input, Select } from "@/components/ui/field";
import { EmptyState, ErrorState, PageLoading, SimilarNote } from "@/components/ui/misc";
import { FilterChips, Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { payError } from "@/features/budget/api";
import { BudgetLayout, useBudgetParams } from "./BudgetLayout";
import { AccountModal, ArchivedAccountsModal, EditMoveModal, MoveModal, RateModal, ReconcileModal, TransferModal, type MoveKind } from "./MoneyModals";

const KIND_ICON: Record<AccountKind, LucideIcon> = { cash: Banknote, card: CreditCard, bank: Landmark };
type Flow = "all" | "in" | "out" | "transfer";

export function MoneyPage() {
  const { t } = useTranslation("budget");
  const { period, branch, isOwner } = useBudgetParams();
  const overview = useFinanceOverview({ period, branch });
  const accountsQ = useMoneyAccounts();
  const [selected, setSelected] = useState<number | null>(null);
  const [move, setMove] = useState<{ kind: MoveKind; account?: number | null } | null>(null);
  const [transferFrom, setTransferFrom] = useState<number | null | undefined>(undefined);
  const [accountEdit, setAccountEdit] = useState<MoneyAccount | null | undefined>(undefined);
  const [rateOpen, setRateOpen] = useState(false);
  const [reconcile, setReconcile] = useState<MoneyAccount | null>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const remove = useDeleteConfirm();

  // Filial tanlangan bo'lsa -- shu filial hisoblari (kompaniya hisoblari umumiy ko'rinishda)
  const accounts = useMemo(() => (accountsQ.data ?? []).filter((a) => !branch || a.branch === branch), [accountsQ.data, branch]);
  const o = overview.data;
  const canManage = isOwner;
  // Arxivdagi hisoblar (owner): tiklash shu yerda -- undan qilingan to'lovni bekor qilish uchun ham kerak
  const archived = useMoneyAccounts(isOwner, true).data ?? [];
  const canDirect = o?.can_direct_expense ?? false;

  const actions = (
    <>
      <Button onClick={() => setMove({ kind: "income", account: selected })} disabled={accounts.length === 0}>
        <ArrowDownLeft size={14} /> {t("money.income")}
      </Button>
      {canDirect && (
        <Button onClick={() => setMove({ kind: "expense", account: selected })} disabled={accounts.length === 0}>
          <ArrowUpRight size={14} /> {t("money.expense")}
        </Button>
      )}
      <Button onClick={() => setTransferFrom(selected)} disabled={accounts.length < 2}>
        <ArrowLeftRight size={14} /> {t("money.transfer")}
      </Button>
    </>
  );

  return (
    <BudgetLayout actions={actions}>
      {overview.isPending || accountsQ.isPending ? (
        <PageLoading />
      ) : overview.isError ? (
        <ErrorState message={overview.error.message} onRetry={() => overview.refetch()} />
      ) : (
        <div className="space-y-4">
          <BalanceStrip o={o!} canManage={canManage} onRate={() => setRateOpen(true)} />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {accounts.map((a) => (
              <ContextMenu
                key={a.id}
                items={() =>
                  accountMenu(a, { manage: canManage, directExpense: canDirect, transfer: accounts.length > 1 }, {
                    income: () => setMove({ kind: "income", account: a.id }),
                    expense: () => setMove({ kind: "expense", account: a.id }),
                    transfer: () => setTransferFrom(a.id),
                    edit: () => setAccountEdit(a),
                    reconcile: () => setReconcile(a),
                    delete: () => void remove(a),
                  }, t)
                }
              >
                <AccountCard account={a} rate={num(o!.rate.usd_uzs)} selected={selected === a.id} onSelect={() => setSelected((s) => (s === a.id ? null : a.id))} onEdit={canManage ? () => setAccountEdit(a) : undefined} />
              </ContextMenu>
            ))}
            {canManage && (
              <button
                type="button"
                onClick={() => setAccountEdit(null)}
                className="flex min-h-[112px] flex-col items-center justify-center gap-1 rounded-card border border-dashed border-border text-[13px] text-ink-soft hover:border-primary hover:text-primary"
              >
                <Plus size={18} />
                {t("money.newAccount")}
              </button>
            )}
          </div>
          {canManage && archived.length > 0 && (
            <button type="button" onClick={() => setArchivedOpen(true)} className="text-[12px] font-medium text-primary hover:underline">
              {t("money.archived.link", { count: archived.length })}
            </button>
          )}
          {accounts.length === 0 && !canManage && <EmptyState title={t("money.noAccountsTitle")} description={t("money.noAccountsManager")} />}

          <Ledger period={period} branch={branch} accounts={accounts} selected={selected} setSelected={setSelected} />
        </div>
      )}

      <MoveModal open={!!move} onOpenChange={(v) => !v && setMove(null)} kind={move?.kind ?? "income"} accounts={accounts} defaultAccount={move?.account} />
      <TransferModal open={transferFrom !== undefined} onOpenChange={(v) => !v && setTransferFrom(undefined)} accounts={accounts} from={transferFrom} />
      {canManage && <AccountModal open={accountEdit !== undefined} onOpenChange={(v) => !v && setAccountEdit(undefined)} account={accountEdit} />}
      {canManage && <RateModal open={rateOpen} onOpenChange={setRateOpen} current={o ? num(o.rate.usd_uzs) || null : null} />}
      {canManage && <ReconcileModal open={!!reconcile} onOpenChange={(v) => !v && setReconcile(null)} account={reconcile} />}
      {canManage && <ArchivedAccountsModal open={archivedOpen} onOpenChange={setArchivedOpen} accounts={archived} />}
    </BudgetLayout>
  );
}

/** Hisobni o'chirish = arxiv (owner): tasdiq bilan, qoldiq bo'lsa ham */
function useDeleteConfirm() {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const del = useDeleteAccount();
  return async (a: MoneyAccount) => {
    const ok = await confirmDialog({
      title: t("money.deleteTitle", { name: a.name }),
      description: t("money.deleteDesc", { balance: fmt(a.balance, a.currency) }),
      confirmLabel: t("money.menu.delete"),
      danger: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(a.id);
      toast.success(t("money.deleted", { name: a.name }));
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
  };
}

// ---- Jami qoldiq va kurs ----

function BalanceStrip({ o, canManage, onRate }: { o: FinanceOverview; canManage: boolean; onRate: () => void }) {
  const { t } = useTranslation("budget");
  const rate = num(o.rate.usd_uzs);
  return (
    <Card className="flex flex-wrap items-center gap-x-8 gap-y-3 px-5 py-4">
      <div>
        <div className="text-[12px] text-ink-soft">{t("money.totalBalance")}</div>
        <div className="mt-0.5 text-[28px] leading-tight font-bold">{fmt(o.total_usd, "USD")}</div>
        <div className="mt-0.5 text-[11px] text-muted">{t("money.totalHint")}</div>
      </div>
      {o.totals.map((x) => (
        <div key={x.currency}>
          <div className="text-[12px] text-ink-soft">{t("money.inCurrency", { currency: x.currency })}</div>
          <div className={cn("mt-0.5 text-[18px] font-semibold", num(x.balance) < 0 && "text-danger")}>{fmt(x.balance, x.currency)}</div>
        </div>
      ))}
      <div className="ml-auto text-right">
        <div className="text-[12px] text-ink-soft">{t("money.rate")}</div>
        <div className="mt-0.5 flex items-center justify-end gap-2 text-[15px] font-semibold">
          {rate ? t("money.rateValue", { rate: rate.toLocaleString("en-US") }) : t("money.noRate")}
          {canManage && (
            <button type="button" onClick={onRate} className="rounded p-1 text-muted hover:bg-fill hover:text-ink" aria-label={t("money.rateTitle")} title={t("money.rateTitle")}>
              <Pencil size={13} />
            </button>
          )}
        </div>
        {o.rate.date && (
          <div className="mt-0.5 text-[11px] text-muted">
            {t(o.rate.source === "manual" ? "money.rateManual" : "money.rateCbu", { date: formatDate(o.rate.date) })}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---- Hisob kartasi ----

/** Hisob kartasi. `ContextMenu` trigger (asChild) prop'larini (onContextMenu, data-state, ref) DOM'ga uzatadi -- aks holda
 * o'ng tugma menyusi ochilmaydi (2026-10-18 gacha shu sabab hisob menyusi ishlamagan). */
const AccountCard = forwardRef<HTMLDivElement, { account: MoneyAccount; rate: number; selected: boolean; onSelect: () => void; onEdit?: () => void } & HTMLAttributes<HTMLDivElement>>(
  function AccountCard({ account: a, rate, selected, onSelect, onEdit, className, ...rest }, ref) {
  const { t } = useTranslation("budget");
  const Icon = KIND_ICON[a.kind];
  const balance = num(a.balance);
  const usd = a.currency === "UZS" && rate ? balance / rate : null;
  return (
    <div
      ref={ref}
      {...rest}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect())}
      aria-pressed={selected}
      title={t("money.filterByAccount")}
      className={cn(
        "group cursor-pointer rounded-card border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/60",
        selected ? "border-primary ring-2 ring-primary/30" : "border-border",
        "data-[state=open]:border-primary",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Icon size={16} strokeWidth={1.75} className="shrink-0 text-ink-soft" aria-hidden />
        <span className="truncate text-[13px] font-semibold">{a.name}</span>
        <span className="ml-auto text-[11px] font-semibold text-muted">{a.currency}</span>
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="hidden rounded p-0.5 text-muted group-hover:block hover:text-ink"
            aria-label={t("money.menu.edit")}
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
      <div className="mt-0.5 truncate text-[11px] text-muted">
        {[t(`money.accountKinds.${a.kind}`), a.bank, a.card_last4 ? `•• ${a.card_last4}` : "", a.branch_name ?? t("money.company")].filter(Boolean).join(" · ")}
      </div>
      <div className={cn("mt-2 text-[22px] leading-tight font-bold", balance < 0 && "text-danger")}>{fmt(balance, a.currency)}</div>
      <div className="text-[11px] text-muted">{usd !== null ? `≈ ${fmt(usd, "USD")}` : " "}</div>
    </div>
  );
});

// ---- Kirim-chiqim daftari ----

function Ledger({ period, branch, accounts, selected, setSelected }: { period: string; branch?: number; accounts: MoneyAccount[]; selected: number | null; setSelected: (v: number | null) => void }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wanted = params.get("flow"); // Budjet umumiy kartasidan: ?flow=in|out|transfer
  const [flow, setFlow] = useState<Flow>(wanted === "in" || wanted === "out" || wanted === "transfer" ? wanted : "all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize("money");
  const [editing, setEditing] = useState<Transaction | null>(null);
  const voidMove = useVoidMove();
  const start = dayjs(`${period}-01`);
  const query = {
    date_from: start.format("YYYY-MM-DD"),
    date_to: start.endOf("month").format("YYYY-MM-DD"),
    account: selected ?? undefined,
    branch,
    direction: flow === "in" || flow === "out" ? flow : undefined,
    kind: flow === "transfer" ? "transfer" : undefined,
    search: search.trim() || undefined,
    page,
    page_size: pageSize,
  };
  const txs = useTransactions(query);
  const selectedAccount = accounts.find((a) => a.id === selected);

  async function cancelMove(tx: Transaction) {
    const linked = tx.salary || tx.bonus ? t("money.voidLinked") : tx.transfer_pair ? t("money.voidTransfer") : "";
    const ok = await confirmDialog({
      title: t("money.voidTitle"),
      description: [t("money.voidDesc", { amount: fmt(tx.amount, tx.currency, tx.direction), account: tx.account_name }), linked].filter(Boolean).join(" "),
      confirmLabel: t("money.menu.void"),
      danger: true,
    });
    if (!ok) return;
    try {
      await voidMove.mutateAsync(tx.id);
      toast.success(t("money.voided"));
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
  }

  const rows = txs.data?.results ?? [];
  return (
    <Card>
      <CardHeader
        title={t("money.ledger", { month: start.format("MMMM YYYY") })}
        subtitle={selectedAccount ? t("money.ledgerFor", { account: selectedAccount.name }) : t("money.ledgerSub")}
        action={
          selectedAccount ? (
            <Button size="sm" variant="link" onClick={() => setSelected(null)}>
              {t("money.allAccounts")}
            </Button>
          ) : undefined
        }
      />
      <CardBody className="space-y-3 pb-0">
        <div className="flex flex-wrap items-center gap-3">
          <FilterChips<Flow>
            items={(["all", "in", "out", "transfer"] as Flow[]).map((k) => ({ key: k, label: t(`money.flow.${k}`) }))}
            value={flow}
            onChange={(k) => {
              setFlow(k);
              setPage(1);
            }}
          />
          <Select
            value={selected ?? ""}
            onChange={(e) => {
              setSelected(e.target.value ? Number(e.target.value) : null);
              setPage(1);
            }}
            className="h-8 w-auto min-w-[160px] text-[12px]"
            aria-label={t("money.f.account")}
          >
            <option value="">{t("money.allAccounts")}</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <div className="relative ml-auto w-[240px] max-w-full">
            <Search size={13} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-muted" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder={t("money.searchPh")}
              aria-label={t("money.searchPh")}
              className="h-8 pl-7 text-[12px]"
            />
          </div>
        </div>
      </CardBody>
      <CardBody className="px-0 pb-0">
        {txs.isPending ? (
          <PageLoading />
        ) : txs.isError ? (
          <ErrorState message={txs.error.message} onRetry={() => txs.refetch()} />
        ) : rows.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState title={t("money.emptyTitle")} description={accounts.length ? t("money.emptyDesc") : t("money.emptyNoAccounts")} />
          </div>
        ) : (
          <>
            <SimilarNote show={txs.data?.fuzzy} className="px-5" />
            <Table variant="flush">
              <thead>
                <tr>
                  <Th className="w-[90px]">{t("money.col.date")}</Th>
                  <Th>{t("money.col.what")}</Th>
                  <Th>{t("money.col.account")}</Th>
                  <Th>{t("money.col.type")}</Th>
                  <Th className="text-right">{t("money.col.amount")}</Th>
                  <Th className="text-right">{t("money.col.usd")}</Th>
                  {selected !== null && <Th className="text-right">{t("money.col.balance")}</Th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((tx) => (
                  <ContextMenu key={tx.id} items={() => transactionMenu(tx, { open: (p) => navigate(p), edit: () => setEditing(tx), void: () => void cancelMove(tx) }, t)}>
                    <Tr>
                      <Td className="whitespace-nowrap text-ink-soft">
                        <div>{formatDate(tx.date)}</div>
                        {tx.at && <div className="text-[11px] text-muted tabular-nums">{timeText(tx.at)}</div>}
                      </Td>
                      <Td>
                        <div className="font-medium">{tx.description || tx.counterparty || categoryLabel(tx, t)}</div>
                        <div className="text-[11px] text-muted">
                          {[tx.description && !tx.transfer_account ? tx.counterparty : "", tx.transfer_account ? t(tx.direction === "out" ? "money.toAcc" : "money.fromAcc", { account: tx.transfer_account }) : "", tx.branch_name, tx.created_by_name].filter(Boolean).join(" · ")}
                        </div>
                      </Td>
                      <Td className="text-ink-soft">{tx.account_name}</Td>
                      <Td>
                        <Badge tone={tx.kind === "transfer" ? "blue" : tx.kind === "adjustment" ? "grey" : tx.direction === "in" ? "green" : "orange"}>{tx.kind === "expense" && tx.category ? tx.category : t(kindKey(tx.kind))}</Badge>
                      </Td>
                      <Td className={cn("text-right font-semibold whitespace-nowrap", tx.direction === "in" ? "text-success" : "text-ink")}>
                        {fmt(tx.amount, tx.currency, tx.direction)}
                        {tx.commission !== null && tx.base_amount !== null && (
                          <div className="text-[11px] font-normal text-muted">
                            {t(tx.direction === "in" ? "commission.rowIn" : "commission.rowOut", { base: fmt(tx.base_amount, tx.currency), pct: num(tx.commission_pct) })}
                          </div>
                        )}
                      </Td>
                      <Td className="text-right whitespace-nowrap text-muted">{tx.currency === "USD" ? "" : tx.amount_usd !== null ? fmt(tx.amount_usd, "USD") : "—"}</Td>
                      {selected !== null && <Td className="text-right whitespace-nowrap text-ink-soft tabular-nums">{tx.balance_after !== null ? fmt(tx.balance_after, tx.currency) : "—"}</Td>}
                    </Tr>
                  </ContextMenu>
                ))}
              </tbody>
            </Table>
            <Pagination page={txs.data!.page} pages={txs.data!.pages} count={txs.data!.count} pageSize={pageSize}
              onPage={setPage}
              onPageSize={(s) => {
                setPageSize(s);
                setPage(1);
              }}
              label={t("money.movements")} className="border-t border-line px-5" />
          </>
        )}
      </CardBody>
      <EditMoveModal tx={editing} onOpenChange={(v) => !v && setEditing(null)} />
    </Card>
  );
}
